<template>
  <div class="c_image_grid_slider" ref="image_grid_slider">
    <div class="c_image_grid_slider__top">
      <div class="c_image_grid_slider__active">
        <transition-group name="c_image_grid_slider-" tag="div">
          <div
            class="c_image_grid_slider__active__content"
            v-for="(image, index) in data"
            :key="`c_image_grid_slider__active--${index}`"
            v-show="index === activeIndex"
            :id="`c_image_grid_slider--${index}`"
          >
            <MediaImage v-if="isMounted(index)" :sources="[image.imageFull]" :alt="image.title" loading="auto" />
            <figcaption v-if="image.caption" v-html="image.caption"></figcaption>
          </div>
        </transition-group>
      </div>
    </div>
    <div class="c_image_grid_slider__bottom">
      <div class="c_image_grid_slider__thumbnails_wrapper" ref="image_grid_thumbnails">
        <ul class="c_image_grid_slider__thumbnails" v-if="['md', 'lg'].includes($mq)">
          <li
            v-for="(thumb, index) in data"
            :key="`c_image_grid_slider__thumbnail--${index}`"
            class="c_image_grid_slider__thumbnails__thumbnail"
            :class="[{ 'c_image_grid_slider__thumbnails__thumbnail--active': index === activeIndex }]"
          >
            <Button
              unstyle
              reset
              @onClick="selectIndex(index)"
              :aria-controls="`c_image_grid_slider--${index}`"
              :ref="(el) => (thumbArray[index] = el)"
              :aria-label="thumb.title"
            >
              <MediaImage :sources="[thumb.image]" :alt="thumb.alt || thumb.title" />
            </Button>
          </li>
        </ul>
      </div>
      <div class="c_image_grid_slider__slider_controls">
        <button
          class="c_image_grid_slider__prev"
          @click="previousSlide()"
          :aria-label="$t('Previous Slide')"
          :disabled="activeIndex === 0"
        >
          <IconSVG handle="arrow_icon_left" width="25" height="19" />
        </button>

        <div class="c_image_grid_slider__slide_indicators">{{ parseInt(activeIndex) + 1 }} / {{ data?.length }}</div>
        <button
          class="c_image_grid_slider__next"
          @click="nextSlide()"
          :disabled="activeIndex === data.length - 1"
          :aria-label="$t('Next Slide')"
        >
          <IconSVG handle="arrow_icon_right" width="25" height="19" />
        </button>
      </div>
    </div>
  </div>
</template>
<script>
import { defineComponent, ref, getCurrentInstance, onMounted, nextTick } from '@nuxtjs/composition-api';
import { useSlidePreloader } from 'Components/composables/useSlidePreloader';
import MediaImage from 'Components/image/MediaImage.vue';
import Button from 'Components/button/Button.vue';
import IconSVG from 'Components/icon_svg/IconSVG.vue';
import { useSwipe } from 'Components/swipe/useSwipe.js';
import { useArrowNavigation } from 'Components/swipe/useArrowNavigation';

export default defineComponent({
  name: 'ImageContentGridSlider',
  components: {
    MediaImage,
    Button,
    IconSVG,
  },
  props: {
    data: { type: Array, default: () => [] },
    activeIndexOverride: { type: Number, default: 0 },
    lazyLoad: { type: Boolean, default: true },
  },
  setup(props) {
    const activeIndex = ref(0);
    const media = props.data ?? [];
    const activeImage = ref(media[0] ?? null);
    const lazyLoad = ref(true);
    const vm = getCurrentInstance();
    const proxy = vm.proxy;
    const isMobile = ref(false);
    const image_grid_thumbnails = ref(null);
    const theme = props.data.theme;

    // Set active image to first in the array from GraphQL data
    activeImage.value = props.activeIndexOverride;
    activeIndex.value = props.activeIndexOverride;

    lazyLoad.value = props.lazyLoad;

    // Dynamic array of thumbnail refs set on template's `v-for`:
    // :ref="(el) => (thumbArray[index] = el)"
    const thumbArray = ref([]);

    const scrollToIndex = async (i, { behavior = 'smooth' } = {}) => {
      await nextTick();

      const thumb = thumbArray.value[i];
      const scrollTarget = thumb?.$el || thumb;

      if (scrollTarget) scrollTarget.focus();
      scrollTarget?.scrollIntoView({ behavior, block: 'nearest', inline: 'center' });
    };

    const selectIndex = async (i) => {
      // Index should never be negative
      if (i < 0) i = 0;

      // Index cannot be greater than length of array
      if (i > props.data.length - 1) i = props.data.length - 1;

      activeImage.value = props.data?.[i];
      activeIndex.value = i;
      await scrollToIndex(i);
    };

    const previousSlide = async () => {
      if (activeIndex.value > 0) {
        const i = activeIndex.value - 1;
        await selectIndex(i);
      }
    };

    const nextSlide = async () => {
      if (activeIndex.value < props.data.length - 1) {
        const i = activeIndex.value + 1;
        await selectIndex(i);
      }
    };

    const preloader = useSlidePreloader({
      items: props.data,
      activeIndex,
      preloadRadius: 1,
      lazyLoad: true,
      getSrc: (item) => {
        const s = item.imageFull;
        return [s?.src || s?.url || s].concat(s?.srcset ? [s.srcset] : []);
      },
    });

    onMounted(() => {
      isMobile.value = proxy.$mq === 'sm';
    });

    // Ref for swipe container
    const image_grid_slider = ref(null);
    useSwipe(image_grid_slider, {
      onSwipe(dir) {
        if (!isMobile.value) return;
        if (dir === 'left') nextSlide();
        else if (dir === 'right') previousSlide();
      },
    });

    // Allow left and right arrows to control
    // `activeIndex` value for accessibility
    useArrowNavigation(nextSlide, previousSlide);

    return {
      activeIndex,
      activeImage,
      theme,
      previousSlide,
      nextSlide,
      image_grid_thumbnails,
      thumbArray,
      selectIndex,
      image_grid_slider,
      lazyLoad,
      ...preloader,
    };
  },
});
</script>
<style lang="scss">
.c_image_grid_slider {
  --image-grid-bottom-height: 100px;
  display: flex;
  flex-direction: column;
  gap: px_to_rem(24);
  &__top {
    height: calc(100dvh - var(--image-grid-bottom-height));
    flex-shrink: 1;
    overflow-y: auto;
    @include mq($mq_sm_to_md) {
      height: 80dvh;
    }
  }
  &__bottom {
    display: flex;
    flex-direction: column;
    height: auto;
    min-height: var(--image-grid-bottom-height);
    flex-shrink: 0;
    gap: px_to_rem(24);
    @include mq($mq_sm_to_md) {
      height: 20dvh;
    }
  }
  &__thumbnails_wrapper {
    width: 100%;
    -webkit-overflow-scrolling: touch;
    max-width: 85vw;
    overflow-x: auto;
    overflow-y: hidden;
    &::-webkit-scrollbar {
      display: none;
    }
  }
  &__thumbnails {
    display: flex;
    flex-direction: row;
    gap: px_to_rem(12);
    width: max-content;

    // `li`
    &__thumbnail {
      position: relative;
      background-color: transparent;
      padding: 0 0 px-to_rem(10) 0;
      border-bottom: 4px solid transparent;
      flex: 0 0 auto;
      overflow: hidden;

      button {
        border-radius: px_to_rem(8);
        border: none;
        width: px_to_rem(174);
        height: px_to_rem(84);
        overflow: hidden;
        position: relative;
        &:after {
          content: '';
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(255, 183, 0, 0);
          transition: background-color ease-in-out 0.15s;
        }
      }
      img {
        object-fit: cover;
      }
      &:hover {
        button::after {
          background-color: rgba(255, 183, 0, 0.4);
        }
      }
      &--active {
        border-bottom: 4px solid var(--color-yellow);
      }
      figure,
      picture {
        height: 100%;
      }
      img {
        height: 100%;
        object-fit: cover;
        object-position: center;
      }
    }
    img {
      object-fit: cover;
      transition: transform ease-in-out 0.15s;
    }
  }

  &__active {
    display: flex;
    height: 100%;
    justify-content: space-around;
    align-items: center;
    color: var(--color-white);
    img {
      width: auto;
      min-height: 0;
      max-height: 60dvh;
      max-width: 100%;
      display: block;
      margin: 0 auto;
      @include mq($mq_sm_to_md) {
        min-height: 50dvh;
      }
    }
    figcaption {
      @include font('graphik_medium');
      @include fs_lh(18, 28, 18, 28);
      max-width: 70ch;
      a {
        color: var(--color-yellow);
        text-decoration: underline;
        &:hover {
          text-decoration: none;
        }
      }
    }
    &__content {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: px_to_rem(24);
    }
  }
  .c_image_grid_slider--enter-active,
  .c_image_grid_slider--leave-active {
    transition: opacity 150ms cubic-bezier(1, 0.5, 0.8, 1);
    will-change: opacity;
  }
  .c_image_grid_slider--enter,
  .c_image_grid_slider--leave-to {
    opacity: 0;
  }
  .c_image_grid_slider--move {
    transition: opacity 150ms cubic-bezier(1, 0.5, 0.8, 1);
    will-change: opacity;
  }
  .c_image_grid_slider--leave-active {
    display: none;
  }
  &__slider_controls {
    @include fs_lh(16, 24, 16, 24);
    @include font('graphik');
    display: flex;
    justify-content: center;
    align-items: center;
    gap: px_to_rem(24);
    color: var(--color-white);
    button {
      @include transition(all 0.1s ease);
      height: px_to_rem(36);
      width: px_to_rem(36);
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9;
      border-radius: 50%;
      background-color: var(--color-yellow);
      border: 1px solid var(--color-yellow);
      color: var(--color-black);
      .c_icon_svg {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      svg {
        width: 100%;
        height: 100%;
      }
      &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }
      &:hover:not(:disabled) {
        background-color: var(--color-yellow-02);
      }
    }
  }
}
</style>
